import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { Phone, MessageCircle, Gauge, Calendar, Fuel, Zap, ArrowLeft, RefreshCw, ShieldCheck, MapPin, Filter, Eraser, X, Undo2, Wand2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---- Admin-Auth: Token aus localStorage laden + bei 401 abmelden ----
const ADMIN_TOKEN_KEY = "mr_admin_token";
const _savedToken = typeof localStorage !== "undefined" ? localStorage.getItem(ADMIN_TOKEN_KEY) : null;
if (_savedToken) axios.defaults.headers.common["Authorization"] = `Bearer ${_savedToken}`;
axios.interceptors.response.use(
  (r) => r,
  (err) => {
    const url = err.config?.url || "";
    // Login-Request NICHT abfangen -> AdminLogin kann die Fehlermeldung anzeigen
    if (err.response && err.response.status === 401 && !url.includes("/admin/login")) {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      delete axios.defaults.headers.common["Authorization"];
      if (window.location.pathname.startsWith("/admin")) window.location.reload();
    }
    return Promise.reject(err);
  }
);

const PHONE = "0155 616 25876";
const PHONE_TEL = "+4915561625876";
const WA = `https://wa.me/4915561625876`;

const img = (path) => (path ? `${BACKEND_URL}${path}` : null);

function Header() {
  const navigate = useNavigate();
  return (
    <header style={{ position: "sticky", top: 0, zIndex: 20, background: "rgba(8,8,8,0.85)", backdropFilter: "blur(14px)", borderBottom: "1px solid var(--border)" }}>
      <div style={{ maxWidth: 1240, margin: "0 auto", padding: "14px 22px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div data-testid="brand-logo" onClick={() => navigate("/")} style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
          <img src="/mr-logo.png" alt="MR Autohaus Germany" style={{ width: 52, height: 52, borderRadius: "50%", boxShadow: "0 0 0 1px var(--gold)" }} />
          <div>
            <div className="font-head gold-text" style={{ fontSize: 20, fontWeight: 700, lineHeight: 1 }}>MR AUTOHAUS</div>
            <div className="font-head" style={{ fontSize: 11, letterSpacing: "0.35em", color: "var(--silver)" }}>GERMANY</div>
          </div>
        </div>
        <a data-testid="header-call-btn" href={`tel:${PHONE_TEL}`} className="btn-gold" style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14 }}>
          <Phone size={16} /> {PHONE}
        </a>
      </div>
    </header>
  );
}

function Hero({ count }) {
  return (
    <section style={{ position: "relative", overflow: "hidden", borderBottom: "1px solid var(--border)" }}>
      <div style={{ maxWidth: 1240, margin: "0 auto", padding: "70px 22px 56px", textAlign: "center" }} className="fade-in">
        <img src="/mr-logo.png" alt="Logo" style={{ width: 120, height: 120, borderRadius: "50%", margin: "0 auto 22px", boxShadow: "0 0 40px rgba(198,160,74,0.25)" }} />
        <h1 className="font-head" style={{ fontSize: "clamp(34px, 6vw, 62px)", fontWeight: 700, lineHeight: 1.05 }}>
          <span className="gold-text">GEPRÜFTE FAHRZEUGE</span><br />ZUM BESTEN PREIS
        </h1>
        <p style={{ color: "var(--muted)", maxWidth: 620, margin: "18px auto 0", fontSize: 17 }}>
          Kaufen · Verkaufen · Finanzierung — auch über das JOBcenter, ohne Anzahlung & mit schneller Zusage.
        </p>
        <div style={{ display: "flex", gap: 14, justifyContent: "center", marginTop: 30, flexWrap: "wrap" }}>
          <a href="#fahrzeuge" className="btn-gold" data-testid="hero-browse-btn">Fahrzeuge ansehen{count ? ` (${count})` : ""}</a>
          <a href={WA} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="hero-whatsapp-btn" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <MessageCircle size={16} /> WhatsApp
          </a>
        </div>
        <div style={{ display: "flex", gap: 26, justifyContent: "center", marginTop: 40, flexWrap: "wrap", color: "var(--silver)" }}>
          {[["Ohne Anzahlung", ShieldCheck], ["JOBcenter Finanzierung", ShieldCheck], ["Schnelle Zusage", ShieldCheck]].map(([t, Icon], i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
              <Icon size={16} color="var(--gold)" /> {t}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CarCard({ car }) {
  const navigate = useNavigate();
  return (
    <div className="car-card fade-in" data-testid={`car-card-${car.ad_id}`} onClick={() => navigate(`/fahrzeug/${car.ad_id}`)}>
      <div style={{ position: "relative", aspectRatio: "4/3", overflow: "hidden", background: "#000" }}>
        {car.image ? (
          <img src={img(car.image)} alt={car.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} loading="lazy" />
        ) : (
          <div style={{ display: "grid", placeItems: "center", height: "100%", color: "var(--muted)" }}>Kein Bild</div>
        )}
        {car.is_new && <span className="badge-new" style={{ position: "absolute", top: 12, left: 12 }}>NEU</span>}
      </div>
      <div style={{ padding: "16px 18px 18px" }}>
        <h3 className="font-head" style={{ fontSize: 17, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{car.title}</h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 10, color: "var(--muted)", fontSize: 13 }}>
          {car.first_registration && <span style={{ display: "flex", alignItems: "center", gap: 5 }}><Calendar size={14} />{car.first_registration}</span>}
          {car.mileage_km && <span style={{ display: "flex", alignItems: "center", gap: 5 }}><Gauge size={14} />{Number(car.mileage_km).toLocaleString("de-DE")} km</span>}
          {car.power_ps && <span style={{ display: "flex", alignItems: "center", gap: 5 }}><Zap size={14} />{car.power_ps} PS</span>}
          {car.fuel && <span style={{ display: "flex", alignItems: "center", gap: 5 }}><Fuel size={14} />{car.fuel}</span>}
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginTop: 14 }}>
          <div className="font-head gold-text" data-testid={`car-price-${car.ad_id}`} style={{ fontSize: 24, fontWeight: 700 }}>{car.display_price_raw || car.price_raw || "Auf Anfrage"}</div>
          {car.monthly_rate && <div style={{ color: "var(--muted)", fontSize: 12 }}>ab {car.monthly_rate}/mtl.</div>}
        </div>
      </div>
    </div>
  );
}

function Home() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fuel, setFuel] = useState("");
  const [make, setMake] = useState("");
  const [makes, setMakes] = useState([]);
  const [sort, setSort] = useState("newest");

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (fuel) params.set("fuel", fuel);
    if (make) params.set("make", make);
    params.set("sort", sort);
    axios.get(`${API}/cars?${params.toString()}`).then((r) => {
      setCars(r.data.cars);
      setLoading(false);
    }).catch(() => setLoading(false));
  };
  useEffect(load, [fuel, make, sort]);
  useEffect(() => { axios.get(`${API}/makes`).then((r) => setMakes(r.data.makes || [])).catch(() => {}); }, []);

  const fuels = [...new Set(cars.map((c) => c.fuel).filter(Boolean))];

  return (
    <>
      <Hero count={cars.length} />
      <main id="fahrzeuge" style={{ maxWidth: 1240, margin: "0 auto", padding: "48px 22px 80px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16, marginBottom: 28 }}>
          <div>
            <h2 className="font-head" style={{ fontSize: 30, fontWeight: 700 }}>UNSERE <span className="gold-text">FAHRZEUGE</span></h2>
            <div className="gold-line" style={{ width: 90, marginTop: 8 }} />
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--muted)" }}><Filter size={16} /></div>
            <select data-testid="filter-make" value={make} onChange={(e) => setMake(e.target.value)}>
              <option value="">Alle Marken</option>
              {makes.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <select data-testid="filter-fuel" value={fuel} onChange={(e) => setFuel(e.target.value)}>
              <option value="">Alle Kraftstoffe</option>
              {fuels.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="newest">Neueste zuerst</option>
              <option value="price_asc">Preis aufsteigend</option>
              <option value="price_desc">Preis absteigend</option>
              <option value="km_asc">Kilometer aufsteigend</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div style={{ display: "grid", placeItems: "center", padding: 80 }}><div className="spinner" /></div>
        ) : cars.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: "var(--muted)" }}>Aktuell keine Fahrzeuge verfügbar.</div>
        ) : (
          <div data-testid="car-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 24 }}>
            {cars.map((c) => <CarCard key={c.ad_id} car={c} />)}
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}

function CarDetail() {
  const { adId } = useParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    axios.get(`${API}/cars/${adId}`).then((r) => { setCar(r.data); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [adId]);

  if (loading) return <div style={{ display: "grid", placeItems: "center", padding: 120 }}><div className="spinner" /></div>;
  if (notFound || !car) return (
    <div style={{ textAlign: "center", padding: 100 }}>
      <p style={{ color: "var(--muted)" }}>Fahrzeug nicht gefunden oder bereits verkauft.</p>
      <button className="btn-gold" style={{ marginTop: 20 }} onClick={() => navigate("/")}>Zurück zur Übersicht</button>
    </div>
  );

  const specs = [
    ["Erstzulassung", car.first_registration, Calendar],
    ["Kilometerstand", car.mileage_km ? `${Number(car.mileage_km).toLocaleString("de-DE")} km` : null, Gauge],
    ["Leistung", car.power_ps ? `${car.power_ps} PS (${car.power_kw} kW)` : null, Zap],
    ["Kraftstoff", car.fuel, Fuel],
  ].filter((s) => s[1]);

  const gallery = (car.gallery && car.gallery.length ? car.gallery : [car.image]).filter(Boolean);
  const mainImg = gallery[Math.min(active, gallery.length - 1)] || car.image;

  const ATTR_LABELS = {
    "Vehicle condition": "Zustand", "Category": "Kategorie", "Mileage": "Kilometerstand",
    "Power": "Leistung", "Fuel": "Kraftstoff", "Transmission": "Getriebe",
    "First Registration": "Erstzulassung", "Cubic Capacity": "Hubraum", "Colour": "Außenfarbe",
    "Door Count": "Türen", "Emission Class": "Schadstoffklasse", "Emissions Sticker": "Umweltplakette",
    "Drive type": "Antriebsart", "Model range": "Modellreihe", "Trim line": "Ausstattungslinie",
    "Climatisation": "Klimatisierung", "Parking sensors": "Einparkhilfe", "Airbags": "Airbags",
    "Interior Design": "Innenausstattung", "HU": "HU", "Seat Count": "Sitzplätze",
    "Weight": "Gewicht", "Number of Vehicle Owners": "Fahrzeughalter", "Construction Year": "Baujahr",
  };
  const attrs = car.attributes || {};
  const attrRows = Object.keys(attrs)
    .filter((k) => attrs[k] && String(attrs[k]).length < 80)
    .slice(0, 24);

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 22px 80px" }} className="fade-in">
      <button className="btn-ghost" data-testid="back-btn" onClick={() => navigate("/")} style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 22 }}>
        <ArrowLeft size={16} /> Zurück
      </button>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 30, minWidth: 0 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ borderRadius: 18, overflow: "hidden", border: "1px solid var(--border)", background: "#000" }}>
            {mainImg ? <img src={img(mainImg)} alt={car.title} style={{ width: "100%", maxHeight: "70vh", objectFit: "contain", display: "block" }} data-testid="detail-image" /> : <div style={{ padding: 80, textAlign: "center", color: "var(--muted)" }}>Kein Bild</div>}
          </div>
          {gallery.length > 1 && (
            <div data-testid="detail-thumbs" style={{ display: "flex", gap: 8, marginTop: 10, overflowX: "auto", paddingBottom: 4 }}>
              {gallery.map((g, i) => (
                <img key={i} src={img(g)} alt="" data-testid={`thumb-${i}`} onClick={() => setActive(i)}
                  style={{ width: 92, height: 68, objectFit: "cover", borderRadius: 8, cursor: "pointer", flexShrink: 0,
                    border: i === active ? "2px solid var(--gold)" : "2px solid transparent", opacity: i === active ? 1 : 0.7 }} />
              ))}
            </div>
          )}
        </div>
        <div style={{ minWidth: 0 }}>
          {car.is_new && <span className="badge-new">NEU</span>}
          <h1 className="font-head" data-testid="detail-title" style={{ fontSize: 30, fontWeight: 700, marginTop: 10 }}>{car.title}</h1>
          <div className="font-head gold-text" style={{ fontSize: 40, fontWeight: 700, marginTop: 6 }}>{car.display_price_raw || car.price_raw || "Auf Anfrage"}</div>
          {car.monthly_rate && <div style={{ color: "var(--muted)", marginTop: 4 }}>Finanzierung ab {car.monthly_rate} / Monat</div>}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12, marginTop: 24 }}>
            {specs.map(([label, val, Icon]) => (
              <div className="spec-chip" key={label}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--gold)", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}><Icon size={14} />{label}</div>
                <div style={{ marginTop: 6, fontSize: 16, fontWeight: 600 }}>{val}</div>
              </div>
            ))}
          </div>

          {car.condition_flags?.length > 0 && (
            <div style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
              {car.condition_flags.map((f) => <span key={f} className="spec-chip" style={{ padding: "6px 12px", fontSize: 13 }}>{f}</span>)}
            </div>
          )}

          {attrRows.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <h3 className="font-head" style={{ fontSize: 18, fontWeight: 600, marginBottom: 10 }}>TECHNISCHE <span className="gold-text">DATEN</span></h3>
              <div data-testid="detail-specs" style={{ border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
                {attrRows.map((k, i) => (
                  <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "9px 14px", fontSize: 14,
                    background: i % 2 ? "var(--bg-soft)" : "transparent" }}>
                    <span style={{ color: "var(--muted)" }}>{ATTR_LABELS[k] || k}</span>
                    <span style={{ fontWeight: 500, textAlign: "right" }}>{String(attrs[k])}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: 14, marginTop: 28, flexWrap: "wrap" }}>
            <a href={`tel:${PHONE_TEL}`} className="btn-gold" data-testid="detail-call-btn" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Phone size={16} /> Anrufen</a>
            <a href={`${WA}?text=${encodeURIComponent("Hallo, ich interessiere mich für: " + car.title)}`} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="detail-whatsapp-btn" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><MessageCircle size={16} /> WhatsApp</a>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Footer() {
  return (
    <footer style={{ borderTop: "1px solid var(--border)", marginTop: 60 }}>
      <div style={{ maxWidth: 1240, margin: "0 auto", padding: "40px 22px", display: "flex", flexWrap: "wrap", gap: 30, justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <img src="/mr-logo.png" alt="Logo" style={{ width: 60, height: 60, borderRadius: "50%" }} />
          <div>
            <div className="font-head gold-text" style={{ fontSize: 18, fontWeight: 700 }}>MR AUTOHAUS GERMANY</div>
            <div style={{ color: "var(--muted)", fontSize: 13, display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}><MapPin size={14} /> Mainz-Kastel</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
          <a href={`tel:${PHONE_TEL}`} className="btn-gold" style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14 }}><Phone size={16} /> {PHONE}</a>
          <a href={WA} target="_blank" rel="noreferrer" className="btn-ghost" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><MessageCircle size={16} /> WhatsApp</a>
        </div>
      </div>
      <div style={{ textAlign: "center", color: "var(--muted)", fontSize: 12, padding: "0 0 24px" }}>
        © {new Date().getFullYear()} MR Autohaus Germany · <Link to="/admin" style={{ color: "var(--muted)" }}>Admin</Link>
      </div>
    </footer>
  );
}

const EVENT_STYLE = {
  added: { c: "#5bd18a", l: "Hinzugefügt" },
  removed: { c: "#e07a5f", l: "Entfernt" },
  plate: { c: "#e8c874", l: "Kennzeichen" },
  block: { c: "#e0a15f", l: "Bild gesperrt" },
  error: { c: "#e05f5f", l: "Fehler" },
  success: { c: "#5bd18a", l: "Erfolg" },
  info: { c: "#8ab4f8", l: "Info" },
};

function AdminNav({ tab, setTab }) {
  const tabs = [["dashboard", "Dashboard"], ["sellers", "Verkäufer"], ["cars", "Fahrzeuge"], ["import", "Import"], ["logs", "Verlauf"], ["smtp", "E-Mail"], ["settings", "Einstellungen"]];
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 28, borderBottom: "1px solid var(--border)", paddingBottom: 14 }}>
      {tabs.map(([k, l]) => (
        <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
          className={tab === k ? "btn-gold" : "btn-ghost"} style={{ padding: "9px 18px", fontSize: 13 }}>{l}</button>
      ))}
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="spec-chip" style={{ padding: 20 }}>
      <div style={{ fontSize: 12, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
      <div className="font-head" style={{ fontSize: 34, fontWeight: 700, marginTop: 6, color: accent || "var(--text)" }}>{value}</div>
    </div>
  );
}

function ApifyKeysCard() {
  const [data, setData] = useState({ keys: [], env_fallback: false });
  const [form, setForm] = useState({ label: "", token: "" });
  const [msg, setMsg] = useState("");
  const [tests, setTests] = useState({});
  const [busy, setBusy] = useState(false);
  const load = () => axios.get(`${API}/admin/apify-keys`).then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.token.trim()) { setMsg("Bitte ein Apify-Token einfügen."); return; }
    setBusy(true); setMsg("");
    try {
      const r = await axios.post(`${API}/admin/apify-keys`, form);
      setMsg(r.data.status === "exists" ? "Dieses Token ist bereits hinterlegt." : "Konto hinzugefügt ✓");
      setForm({ label: "", token: "" }); load();
    } catch (e) { setMsg(e.response?.data?.detail || "Fehler beim Hinzufügen"); }
    setBusy(false);
  };
  const del = async (id) => { if (window.confirm("Dieses Apify-Konto entfernen?")) { await axios.delete(`${API}/admin/apify-keys/${id}`); load(); } };
  const move = async (id, dir) => { await axios.post(`${API}/admin/apify-keys/${id}/move?dir=${dir}`); load(); };
  const test = async (id) => {
    setTests((t) => ({ ...t, [id]: { loading: true } }));
    try { const r = await axios.post(`${API}/admin/apify-keys/${id}/test`); setTests((t) => ({ ...t, [id]: r.data })); }
    catch (e) { setTests((t) => ({ ...t, [id]: { ok: false, error: "Fehler" } })); }
  };

  return (
    <div className="spec-chip" data-testid="apify-keys-card" style={{ padding: 24, marginBottom: 20 }}>
      <div className="font-head" style={{ fontSize: 18, fontWeight: 600 }}>Apify-Konten</div>
      <div style={{ color: "var(--muted)", fontSize: 13, marginTop: 4, marginBottom: 16 }}>
        Mehrere Konten möglich. Der Import nutzt sie in dieser Reihenfolge und wechselt automatisch zum nächsten, wenn ein Konto kein Guthaben mehr hat.
      </div>
      <div style={{ display: "grid", gap: 10, marginBottom: 16 }}>
        {data.keys.map((k, i) => {
          const t = tests[k.id];
          return (
            <div key={k.id} data-testid={`apify-key-${k.id}`} className="spec-chip" style={{ padding: 12, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, color: "var(--muted)", minWidth: 22 }}>#{i + 1}</span>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{k.label}</div>
                <div style={{ color: "var(--muted)", fontSize: 12, fontFamily: "monospace" }}>{k.token_masked}</div>
                {t && !t.loading && (
                  <div style={{ fontSize: 12, marginTop: 3, color: t.ok ? "#5bd18a" : "#e05f5f" }}>
                    {t.ok ? `gültig${t.username ? ` · ${t.username}` : ""}${t.plan ? ` · ${t.plan}` : ""}${t.usage_usd != null ? ` · Nutzung $${t.usage_usd}${t.limit_usd ? "/$" + t.limit_usd : ""}` : ""}` : `✗ ${t.error || "ungültig"}`}
                  </div>
                )}
                {t?.loading && <div style={{ fontSize: 12, marginTop: 3, color: "var(--muted)" }}>prüfe…</div>}
              </div>
              <button className="btn-ghost" data-testid={`apify-test-${k.id}`} onClick={() => test(k.id)} style={{ padding: "6px 12px", fontSize: 12 }}>Prüfen</button>
              <button className="btn-ghost" data-testid={`apify-up-${k.id}`} onClick={() => move(k.id, "up")} disabled={i === 0} style={{ padding: "6px 10px", fontSize: 12 }}>↑</button>
              <button className="btn-ghost" data-testid={`apify-down-${k.id}`} onClick={() => move(k.id, "down")} disabled={i === data.keys.length - 1} style={{ padding: "6px 10px", fontSize: 12 }}>↓</button>
              <button className="btn-ghost" data-testid={`apify-del-${k.id}`} onClick={() => del(k.id)} style={{ padding: "6px 12px", fontSize: 12, color: "#e05f5f", borderColor: "#e05f5f" }}>Entfernen</button>
            </div>
          );
        })}
        {data.keys.length === 0 && <div style={{ color: "var(--muted)", fontSize: 13 }}>Noch kein Apify-Konto hinterlegt.</div>}
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "end", flexWrap: "wrap" }}>
        <div><label style={{ fontSize: 11, color: "var(--muted)" }}>Bezeichnung (optional)</label><input data-testid="apify-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="z. B. Haupt-Konto" style={{ width: 180 }} /></div>
        <div style={{ flex: 1, minWidth: 220 }}><label style={{ fontSize: 11, color: "var(--muted)" }}>Apify API-Token</label><input data-testid="apify-token" value={form.token} onChange={(e) => setForm({ ...form, token: e.target.value })} placeholder="apify_api_…" style={{ width: "100%" }} /></div>
        <button className="btn-gold" data-testid="apify-add-btn" onClick={add} disabled={busy} style={{ padding: "10px 18px" }}>Konto hinzufügen</button>
      </div>
      {msg && <div data-testid="apify-msg" style={{ marginTop: 10, fontSize: 13, color: "var(--gold-bright)" }}>{msg}</div>}
      <div style={{ marginTop: 10, fontSize: 12, color: "var(--muted)" }}>Token findest du auf apify.com → Settings → Integrations → API token.</div>
    </div>
  );
}

function ImportPanel({ status, onSync }) {
  const [prog, setProg] = useState(null);
  const [stopping, setStopping] = useState(false);
  useEffect(() => {
    const load = () => axios.get(`${API}/admin/progress`).then((r) => setProg(r.data));
    load();
    const t = setInterval(load, 1500);
    return () => clearInterval(t);
  }, []);
  const running = prog?.running || status?.running;
  const pct = prog?.total ? Math.round((prog.current / prog.total) * 100) : 0;
  const stop = async () => { setStopping(true); await axios.post(`${API}/admin/stop`); };
  useEffect(() => { if (!running) setStopping(false); }, [running]);
  return (
    <div className="fade-in">
      <ApifyKeysCard />
      <div className="spec-chip" style={{ padding: 24, marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <div>
            <div className="font-head" style={{ fontSize: 20, fontWeight: 600 }}>Manueller Import</div>
            <div style={{ color: "var(--muted)", fontSize: 13, marginTop: 4 }}>Holt Fahrzeuge von mobile.de, bearbeitet Bilder & aktualisiert die Seite.</div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn-gold" data-testid="admin-sync-btn" onClick={onSync} disabled={running}
              style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
              <RefreshCw size={16} style={{ animation: running ? "spin 0.9s linear infinite" : "none" }} />
              {running ? "Läuft…" : "Jetzt importieren"}
            </button>
            {running && (
              <button className="btn-ghost" data-testid="admin-stop-btn" onClick={stop} disabled={stopping}
                style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "#e05f5f", borderColor: "#e05f5f" }}>
                <X size={16} /> {stopping ? "Wird gestoppt…" : "Stoppen"}
              </button>
            )}
          </div>
        </div>
        {running && (
          <div style={{ marginTop: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--silver)", marginBottom: 6 }}>
              <span>{prog?.phase || "…"}</span><span>{prog?.current}/{prog?.total} ({pct}%)</span>
            </div>
            <div style={{ height: 8, background: "var(--bg-soft)", borderRadius: 999, overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: "linear-gradient(90deg, var(--gold), var(--gold-bright))", transition: "width 0.4s ease" }} />
            </div>
          </div>
        )}
      </div>
      <div className="spec-chip" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)", fontWeight: 600 }} className="font-head">Live-Protokoll</div>
        <div data-testid="import-log" style={{ maxHeight: 380, overflowY: "auto" }}>
          {(prog?.events || []).slice().reverse().map((e, i) => {
            const st = EVENT_STYLE[e.kind] || EVENT_STYLE.info;
            return (
              <div key={i} style={{ display: "flex", gap: 12, padding: "10px 18px", borderBottom: "1px solid rgba(255,255,255,0.03)", fontSize: 13 }}>
                <span style={{ color: st.c, fontWeight: 600, minWidth: 96 }}>{st.l}</span>
                <span style={{ color: "var(--silver)" }}>{e.message}</span>
                <span style={{ marginLeft: "auto", color: "var(--muted)", fontSize: 11, whiteSpace: "nowrap" }}>{new Date(e.time).toLocaleTimeString("de-DE")}</span>
              </div>
            );
          })}
          {(!prog?.events || prog.events.length === 0) && <div style={{ padding: 20, color: "var(--muted)" }}>Noch keine Aktivität.</div>}
        </div>
      </div>
    </div>
  );
}

function RetouchModal({ adId, item, onClose, onDone }) {
  const [boxes, setBoxes] = useState([]);
  const [cur, setCur] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [url, setUrl] = useState(item.url);
  const ref = useRef(null);

  const norm = (e) => {
    const r = ref.current.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)),
    };
  };
  const down = (e) => { e.preventDefault(); const p = norm(e); setCur({ x0: p.x, y0: p.y, x1: p.x, y1: p.y }); };
  const move = (e) => { if (!cur) return; const p = norm(e); setCur({ ...cur, x1: p.x, y1: p.y }); };
  const up = () => {
    if (!cur) return;
    const w = Math.abs(cur.x1 - cur.x0), h = Math.abs(cur.y1 - cur.y0);
    if (w > 0.01 && h > 0.01) setBoxes([...boxes, [Math.min(cur.x0, cur.x1), Math.min(cur.y0, cur.y1), w, h]]);
    setCur(null);
  };
  const apply = async () => {
    if (!boxes.length) { setErr("Bitte zuerst Bereiche markieren (mit der Maus ziehen)."); return; }
    setBusy(true); setErr("");
    try {
      const r = await axios.post(`${API}/admin/cars/${adId}/images/${item.idx}/retouch`, { boxes });
      const newItem = (r.data.media_public || []).find((m) => m.idx === item.idx);
      if (newItem) setUrl(newItem.url);
      setBoxes([]);
      onDone(r.data);
    } catch (e) { setErr(e.response?.data?.detail || "Retusche fehlgeschlagen"); }
    setBusy(false);
  };
  const boxStyle = (x, y, w, h) => ({
    position: "absolute", left: `${x * 100}%`, top: `${y * 100}%`, width: `${w * 100}%`, height: `${h * 100}%`,
    border: "2px solid var(--gold)", background: "rgba(198,160,74,0.25)", pointerEvents: "none",
  });

  return (
    <div data-testid="retouch-modal" onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(0,0,0,0.88)", display: "grid", placeItems: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} className="spec-chip" style={{ padding: 18, maxWidth: "94vw" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 12 }}>
          <div>
            <div className="font-head" style={{ fontWeight: 700, fontSize: 16 }}>KI-Retusche — Bereiche entfernen</div>
            <div style={{ color: "var(--muted)", fontSize: 12 }}>Ziehe Rechtecke über störende Schilder/Logos/Texte, dann „Entfernen".</div>
          </div>
          <button data-testid="retouch-close" className="btn-ghost" onClick={onClose} style={{ padding: "6px 10px" }}><X size={16} /></button>
        </div>
        <div ref={ref} onMouseDown={down} onMouseMove={move} onMouseUp={up} onMouseLeave={up}
          style={{ position: "relative", display: "inline-block", cursor: "crosshair", userSelect: "none", lineHeight: 0, maxWidth: "100%" }}>
          <img src={img(url)} alt="" draggable={false} data-testid="retouch-image" style={{ display: "block", maxWidth: "100%", maxHeight: "64vh", pointerEvents: "none" }} />
          {boxes.map((b, i) => <div key={i} style={boxStyle(b[0], b[1], b[2], b[3])} />)}
          {cur && <div style={boxStyle(Math.min(cur.x0, cur.x1), Math.min(cur.y0, cur.y1), Math.abs(cur.x1 - cur.x0), Math.abs(cur.y1 - cur.y0))} />}
          {busy && <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", background: "rgba(0,0,0,0.55)" }}><div className="spinner" /></div>}
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 12, alignItems: "center", flexWrap: "wrap" }}>
          <button data-testid="retouch-apply" className="btn-gold" onClick={apply} disabled={busy || !boxes.length} style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <Wand2 size={16} />{busy ? "KI arbeitet…" : `Entfernen (${boxes.length})`}
          </button>
          <button data-testid="retouch-undo" className="btn-ghost" onClick={() => setBoxes(boxes.slice(0, -1))} disabled={busy || !boxes.length} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Undo2 size={15} /> Rückgängig</button>
          <button data-testid="retouch-clear" className="btn-ghost" onClick={() => setBoxes([])} disabled={busy || !boxes.length}>Alle</button>
        </div>
        {err && <div style={{ marginTop: 8, color: "#e05f5f", fontSize: 13 }} data-testid="retouch-error">{err}</div>}
      </div>
    </div>
  );
}

function CarsPanel() {
  const [cars, setCars] = useState([]);
  const [open, setOpen] = useState(null);
  const [retouch, setRetouch] = useState(null);
  const [msg, setMsg] = useState("");
  const [sellerFilter, setSellerFilter] = useState("all");
  const load = () => axios.get(`${API}/admin/cars`).then((r) => setCars(r.data.cars));
  useEffect(() => { load(); }, []);

  const setPrice = async (ad_id, val) => {
    await axios.patch(`${API}/admin/cars/${ad_id}`, { price_override: val ? parseInt(val) : 0 });
    load();
  };
  const toggleHidden = async (ad_id, hidden) => {
    await axios.patch(`${API}/admin/cars/${ad_id}`, { hidden });
    load();
  };
  const toggleImage = async (ad_id, idx, blocked) => {
    await axios.patch(`${API}/admin/cars/${ad_id}/images/${idx}`, { blocked });
    load();
  };
  const reprocessCar = async (ad_id, title) => {
    const r = await axios.post(`${API}/admin/cars/${ad_id}/reprocess`);
    if (r.data.status === "already_running") { setMsg("Eine Bearbeitung läuft bereits – bitte warten."); return; }
    setMsg(`KI-Neubearbeitung gestartet für „${(title || "").slice(0, 30)}" … (Fortschritt im Dashboard)`);
    setTimeout(load, 4000);
  };
  const deleteCar = async (ad_id, title) => {
    if (window.confirm(`Fahrzeug „${(title || "").slice(0, 40)}" wirklich löschen? (inkl. Bilder)`)) {
      await axios.delete(`${API}/admin/cars/${ad_id}`);
      setMsg(`Fahrzeug gelöscht.`); load();
    }
  };

  const sellerNames = [...new Set(cars.map((c) => c.seller_name).filter(Boolean))];
  const shown = sellerFilter === "all" ? cars : cars.filter((c) => c.seller_name === sellerFilter);

  return (
    <div className="fade-in" data-testid="admin-cars-panel">
      <div style={{ color: "var(--muted)", fontSize: 13, marginBottom: 14 }}>Preis überschreiben (leer = Aufschlag) · aus-/einblenden · „Bilder" öffnen zum Sperren/Freigeben · „KI neu" bearbeitet ein Auto erneut, falls die Bildbearbeitung Fehler hat.</div>
      {msg && <div data-testid="cars-msg" style={{ marginBottom: 12, padding: "8px 12px", borderRadius: 8, background: "rgba(198,160,74,0.12)", border: "1px solid var(--gold)", fontSize: 13 }}>{msg}</div>}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: "var(--muted)" }}>Verkäufer:</span>
        <select data-testid="cars-seller-filter" value={sellerFilter} onChange={(e) => setSellerFilter(e.target.value)} style={{ padding: "6px 10px", fontSize: 12 }}>
          <option value="all">Alle ({cars.length})</option>
          {sellerNames.map((n) => <option key={n} value={n}>{n} ({cars.filter((c) => c.seller_name === n).length})</option>)}
        </select>
      </div>
      <div style={{ display: "grid", gap: 12 }}>
        {shown.map((c) => (
          <div key={c.ad_id} className="spec-chip" style={{ padding: 12, opacity: c.hidden ? 0.6 : 1 }}>
            <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
              <img src={img(c.image)} alt="" style={{ width: 90, height: 66, objectFit: "cover", borderRadius: 8, background: "#000" }} />
              <div style={{ flex: 1, minWidth: 180 }}>
                <div className="font-head" style={{ fontWeight: 600, fontSize: 15 }}>{c.title}</div>
                <div style={{ color: "var(--muted)", fontSize: 12 }}>{c.seller_name} · Original: {c.price_raw} · Anzeige: <span style={{ color: "var(--gold-bright)" }}>{c.display_price_raw}</span> · {c.gallery?.length || 0} Bilder</div>
              </div>
              <input data-testid={`price-input-${c.ad_id}`} type="number" placeholder="Eigener €" defaultValue={c.price_override || ""} style={{ width: 120 }}
                onBlur={(e) => setPrice(c.ad_id, e.target.value)} />
              <button className="btn-ghost" data-testid={`reprocess-car-${c.ad_id}`} onClick={() => reprocessCar(c.ad_id, c.title)} style={{ padding: "8px 14px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5 }}>
                <RefreshCw size={13} /> KI neu
              </button>
              <button className="btn-ghost" data-testid={`images-btn-${c.ad_id}`} onClick={() => setOpen(open === c.ad_id ? null : c.ad_id)} style={{ padding: "8px 14px", fontSize: 12 }}>Bilder ({c.media_public?.length || 0})</button>
              <button className={c.hidden ? "btn-gold" : "btn-ghost"} data-testid={`toggle-hidden-${c.ad_id}`} onClick={() => toggleHidden(c.ad_id, !c.hidden)} style={{ padding: "8px 14px", fontSize: 12 }}>
                {c.hidden ? "Einblenden" : "Ausblenden"}
              </button>
              <button className="btn-ghost" data-testid={`delete-car-${c.ad_id}`} onClick={() => deleteCar(c.ad_id, c.title)} style={{ padding: "8px 14px", fontSize: 12, color: "#e05f5f", borderColor: "#e05f5f" }}>Löschen</button>
            </div>
            {open === c.ad_id && (
              <div data-testid={`image-manager-${c.ad_id}`} style={{ marginTop: 12, borderTop: "1px solid var(--border)", paddingTop: 12, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 10 }}>
                {(c.media_public || []).map((m) => (
                  <div key={m.idx} style={{ position: "relative", borderRadius: 8, overflow: "hidden", border: m.blocked ? "2px solid #e05f5f" : "2px solid transparent" }}>
                    {m.url ? <img src={img(m.url)} alt="" style={{ width: "100%", height: 90, objectFit: "cover", filter: m.blocked ? "grayscale(1) brightness(0.5)" : "none" }} />
                      : <div style={{ height: 90, display: "grid", placeItems: "center", background: "#1a1a1a", color: "#e05f5f", fontSize: 11, textAlign: "center", padding: 4 }}>Gesperrt<br />(Werbebild)</div>}
                    {!m.blocked && m.url && (
                      <button data-testid={`img-retouch-${c.ad_id}-${m.idx}`} onClick={() => setRetouch({ adId: c.ad_id, item: m })}
                        style={{ position: "absolute", top: 4, left: 4, fontSize: 10, padding: "3px 7px", borderRadius: 6, border: "none", cursor: "pointer",
                          background: "rgba(198,160,74,0.92)", color: "#1a1400", display: "inline-flex", alignItems: "center", gap: 3, fontWeight: 600 }}>
                        <Eraser size={11} /> Retusche
                      </button>
                    )}
                    <button data-testid={`img-block-${c.ad_id}-${m.idx}`} onClick={() => toggleImage(c.ad_id, m.idx, !m.blocked)}
                      style={{ position: "absolute", bottom: 4, left: 4, right: 4, fontSize: 10, padding: "3px 0", borderRadius: 6, border: "none", cursor: "pointer",
                        background: m.blocked ? "var(--gold)" : "rgba(0,0,0,0.7)", color: m.blocked ? "#1a1400" : "#fff" }}>
                      {m.blocked ? "Freigeben" : "Sperren"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      {retouch && (
        <RetouchModal adId={retouch.adId} item={retouch.item}
          onClose={() => setRetouch(null)}
          onDone={(updated) => {
            setCars((cs) => cs.map((c) => (c.ad_id === updated.ad_id ? updated : c)));
            setRetouch((r) => (r ? { ...r, item: (updated.media_public || []).find((m) => m.idx === r.item.idx) || r.item } : r));
          }} />
      )}
    </div>
  );
}

function SellersPanel({ status }) {
  const [sellers, setSellers] = useState([]);
  const [form, setForm] = useState({ name: "", url: "", platform: "mobile_de", markup_percent: 0, round_to: 100, replace_plate: true, remove_bg_text: false, studio_bg: false });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = () => axios.get(`${API}/admin/sellers`).then((r) => setSellers(r.data.sellers));
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);

  const add = async () => {
    if (!form.name || !form.url) { setMsg("Name und URL erforderlich"); return; }
    setBusy(true); setMsg("Verkäufer wird geprüft…");
    try {
      await axios.post(`${API}/admin/sellers`, { ...form, markup_percent: parseFloat(form.markup_percent), round_to: parseInt(form.round_to) });
      setForm({ name: "", url: "", platform: "mobile_de", markup_percent: 0, round_to: 100, replace_plate: true, remove_bg_text: false, studio_bg: false }); setMsg("Verkäufer hinzugefügt ✓"); load();
    } catch (e) { setMsg(e.response?.data?.detail || "Fehler beim Hinzufügen"); }
    setBusy(false);
  };
  const saveMarkup = async (id, markup, round_to) => { await axios.put(`${API}/admin/sellers/${id}`, { markup_percent: parseFloat(markup), round_to: parseInt(round_to) }); load(); };
  const savePlate = async (id, replace_plate) => { await axios.put(`${API}/admin/sellers/${id}`, { replace_plate }); load(); };
  const saveBg = async (id, remove_bg_text) => { await axios.put(`${API}/admin/sellers/${id}`, { remove_bg_text }); load(); };
  const saveStudio = async (id, studio_bg) => { await axios.put(`${API}/admin/sellers/${id}`, { studio_bg }); load(); };
  const del = async (id) => { if (window.confirm("Verkäufer + zugehörige Fahrzeuge löschen?")) { await axios.delete(`${API}/admin/sellers/${id}`); load(); } };
  const delCars = async (id, name) => {
    if (window.confirm(`Wirklich ALLE Fahrzeuge von „${name}" löschen? (Verkäufer bleibt bestehen, Bilder werden entfernt)`)) {
      const r = await axios.post(`${API}/admin/sellers/${id}/cars/delete`);
      setMsg(`${r.data.deleted ?? 0} Fahrzeuge von „${name}" gelöscht.`); load();
    }
  };
  const reprocessSeller = async (id, name) => {
    const r = await axios.post(`${API}/admin/sellers/${id}/reprocess`);
    if (r.data.status === "already_running") { setMsg("Eine Bearbeitung läuft bereits – bitte warten."); return; }
    if (r.data.status === "no_cars") { setMsg(`„${name}" hat keine Fahrzeuge zum Bearbeiten.`); return; }
    setMsg(`KI-Neubearbeitung aller Fahrzeuge von „${name}" gestartet – Fortschritt im Dashboard.`);
  };
  const runImport = async (id, limit) => { await axios.post(`${API}/admin/sellers/${id}/sync${limit ? `?limit=${limit}` : ""}`); setMsg(`${limit ? "Test-" : "Voll-"}Import gestartet…`); };

  const running = status?.running;
  return (
    <div className="fade-in" data-testid="admin-sellers-panel">
      <div className="spec-chip" style={{ padding: 20, marginBottom: 20 }}>
        <div className="font-head" style={{ fontSize: 18, fontWeight: 600, marginBottom: 10 }}>Neuen Verkäufer verbinden</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.1fr 1.8fr 0.7fr 0.7fr auto", gap: 10, alignItems: "end", flexWrap: "wrap" }}>
          <div><label style={{ fontSize: 11, color: "var(--muted)" }}>Plattform</label>
            <select data-testid="seller-platform" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} style={{ width: "100%" }}>
              <option value="mobile_de">mobile.de</option>
              <option value="kleinanzeigen">Kleinanzeigen</option>
            </select>
          </div>
          <div><label style={{ fontSize: 11, color: "var(--muted)" }}>Name</label><input data-testid="seller-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={{ width: "100%" }} placeholder="Autohaus XY" /></div>
          <div><label style={{ fontSize: 11, color: "var(--muted)" }}>{form.platform === "kleinanzeigen" ? "Kleinanzeigen Profil-/Store-URL" : "mobile.de Händler-URL"}</label><input data-testid="seller-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} style={{ width: "100%" }} placeholder={form.platform === "kleinanzeigen" ? "https://www.kleinanzeigen.de/s-bestandsliste.html?userId=… oder /pro/NAME" : "https://home.mobile.de/NAME"} /></div>
          <div><label style={{ fontSize: 11, color: "var(--muted)" }}>Aufschlag %</label><input data-testid="seller-markup" type="number" value={form.markup_percent} onChange={(e) => setForm({ ...form, markup_percent: e.target.value })} style={{ width: "100%" }} /></div>
          <div><label style={{ fontSize: 11, color: "var(--muted)" }}>Runden €</label><input type="number" value={form.round_to} onChange={(e) => setForm({ ...form, round_to: e.target.value })} style={{ width: "100%" }} /></div>
          <button className="btn-gold" data-testid="seller-add-btn" onClick={add} disabled={busy} style={{ padding: "10px 18px" }}>Hinzufügen</button>
        </div>
        {msg && <div style={{ marginTop: 10, fontSize: 13, color: "var(--gold-bright)" }}>{msg}</div>}
        <label style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--silver)", cursor: "pointer" }}>
          <input type="checkbox" data-testid="seller-replace-plate" checked={form.replace_plate} onChange={(e) => setForm({ ...form, replace_plate: e.target.checked })} />
          Werbkennzeichen automatisch durch MR-Autohaus-Schild ersetzen
        </label>
        <label style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--silver)", cursor: "pointer" }}>
          <input type="checkbox" data-testid="seller-remove-bg" checked={form.remove_bg_text} onChange={(e) => setForm({ ...form, remove_bg_text: e.target.checked })} />
          Große fremde Gebäudeschilder/Logos im Hintergrund automatisch entfernen (KI)
        </label>
        <label style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--gold-bright)", cursor: "pointer" }}>
          <input type="checkbox" data-testid="seller-studio-bg" checked={form.studio_bg} onChange={(e) => setForm({ ...form, studio_bg: e.target.checked })} />
          ⭐ Studio-Hintergrund: Auto automatisch freistellen &amp; auf sauberen Premium-Hintergrund setzen (entfernt ALLE fremden Logos; Innenraum-Fotos bleiben unangetastet)
        </label>
        <div style={{ marginTop: 8, fontSize: 12, color: "var(--muted)" }}>
          Tipp mobile.de: Händler-Link (home.mobile.de/NAME), Fahrzeug-Listen-Link (suchen.mobile.de …?sid=/customerId=) oder Verkäufer-Nummer. — Kleinanzeigen: Profil-URL mit „…userId=…" oder Store-URL „…/pro/NAME".
        </div>
      </div>

      <div style={{ display: "grid", gap: 12 }}>
        {sellers.map((s) => (
          <div key={s.id} className="spec-chip" data-testid={`seller-${s.id}`} style={{ padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
              <div>
                <div className="font-head" style={{ fontWeight: 700, fontSize: 16 }}>{s.name} <span style={{ color: "var(--muted)", fontSize: 12 }}>· {s.car_count} Fahrzeuge · <span style={{ color: s.platform === "kleinanzeigen" ? "#4a9eff" : "var(--gold-bright)" }}>{s.platform === "kleinanzeigen" ? "Kleinanzeigen" : "mobile.de"}</span> · id {s.sid}</span></div>
                <div style={{ color: "var(--muted)", fontSize: 12 }}>{s.input_url}</div>
                {s.last_sync && <div style={{ fontSize: 12, color: s.last_sync.status === "success" ? "#5bd18a" : "#e05f5f" }}>Letzter Import: {s.last_sync.status} · {s.last_sync.added ?? 0} neu, {s.last_sync.removed ?? 0} entfernt</div>}
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, color: "var(--muted)" }}>Aufschlag</span>
                <input data-testid={`seller-markup-${s.id}`} type="number" defaultValue={s.markup_percent} style={{ width: 70 }}
                  onBlur={(e) => saveMarkup(s.id, e.target.value, s.round_to)} /> %
                <label style={{ marginLeft: 14, display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--silver)", cursor: "pointer" }}>
                  <input type="checkbox" data-testid={`seller-plate-${s.id}`} checked={!!s.replace_plate} onChange={(e) => savePlate(s.id, e.target.checked)} />
                  MR-Kennzeichen
                </label>
                <label style={{ marginLeft: 10, display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--silver)", cursor: "pointer" }}>
                  <input type="checkbox" data-testid={`seller-bg-${s.id}`} checked={!!s.remove_bg_text} onChange={(e) => saveBg(s.id, e.target.checked)} />
                  Schilder entfernen
                </label>
                <label style={{ marginLeft: 10, display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--gold-bright)", cursor: "pointer" }}>
                  <input type="checkbox" data-testid={`seller-studio-${s.id}`} checked={!!s.studio_bg} onChange={(e) => saveStudio(s.id, e.target.checked)} />
                  ⭐ Studio-BG
                </label>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
              {[1, 3, 5].map((n) => (
                <button key={n} className="btn-ghost" data-testid={`seller-test-${s.id}-${n}`} disabled={running} onClick={() => runImport(s.id, n)} style={{ padding: "7px 14px", fontSize: 12 }}>Test {n}</button>
              ))}
              <button className="btn-gold" data-testid={`seller-full-${s.id}`} disabled={running} onClick={() => runImport(s.id, null)} style={{ padding: "7px 16px", fontSize: 12 }}>Voll-Import</button>
              <button className="btn-ghost" data-testid={`seller-reprocess-${s.id}`} disabled={running} onClick={() => reprocessSeller(s.id, s.name)} style={{ padding: "7px 14px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5 }}><RefreshCw size={13} /> KI neu bearbeiten</button>
              <button className="btn-ghost" data-testid={`seller-delcars-${s.id}`} onClick={() => delCars(s.id, s.name)} style={{ padding: "7px 14px", fontSize: 12, color: "#e0a05f", borderColor: "#e0a05f" }}>Nur Autos löschen</button>
              <button className="btn-ghost" data-testid={`seller-del-${s.id}`} onClick={() => del(s.id)} style={{ padding: "7px 14px", fontSize: 12, color: "#e05f5f", borderColor: "#e05f5f" }}>Löschen</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SmtpPanel() {
  const [s, setS] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [msg, setMsg] = useState("");
  const load = () => { axios.get(`${API}/admin/smtp`).then((r) => setS(r.data)); axios.get(`${API}/admin/notifications`).then((r) => setNotifs(r.data.notifications)); };
  useEffect(() => { load(); }, []);
  const save = async () => { await axios.put(`${API}/admin/smtp`, s); setMsg("Gespeichert ✓"); setTimeout(() => setMsg(""), 2000); };
  const test = async () => { setMsg("Test-Mail wird gesendet…"); const r = await axios.post(`${API}/admin/smtp/test`); setMsg(r.data.ok ? "Test-Mail gesendet ✓" : `Fehler: ${r.data.error}`); load(); };
  if (!s) return <div className="spinner" />;
  const F = (k, label, type = "text") => (
    <div><label style={{ fontSize: 12, color: "var(--gold)" }}>{label}</label>
      <input data-testid={`smtp-${k}`} type={type} value={s[k] ?? ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} style={{ width: "100%", marginTop: 4 }} /></div>
  );
  return (
    <div className="fade-in" data-testid="admin-smtp-panel" style={{ maxWidth: 640 }}>
      <div className="spec-chip" style={{ padding: 24 }}>
        <div className="font-head" style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>SMTP / E-Mail-Benachrichtigung</div>
        <div style={{ color: "var(--muted)", fontSize: 13, marginBottom: 16 }}>Nach jedem Import bekommst du eine E-Mail mit Bericht (was neu/entfernt/bearbeitet wurde).</div>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
          {F("host", "SMTP-Host")}{F("port", "Port", "number")}
          {F("username", "Benutzername")}{F("password", "Passwort", "password")}
          {F("from_email", "Absender-E-Mail")}{F("to_emails", "Empfänger (Komma-getrennt)")}
        </div>
        <div style={{ display: "flex", gap: 16, marginTop: 14, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 14 }}>
            <input data-testid="smtp-enabled" type="checkbox" checked={!!s.enabled} onChange={(e) => setS({ ...s, enabled: e.target.checked })} /> Benachrichtigungen aktiv
          </label>
          <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 14 }}>
            <input type="checkbox" checked={s.use_tls !== false} onChange={(e) => setS({ ...s, use_tls: e.target.checked })} /> STARTTLS
          </label>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
          <button className="btn-gold" data-testid="smtp-save-btn" onClick={save}>Speichern</button>
          <button className="btn-ghost" data-testid="smtp-test-btn" onClick={test}>Test-Mail senden</button>
        </div>
        {msg && <div style={{ marginTop: 12, color: "var(--gold-bright)", fontSize: 13 }}>{msg}</div>}
      </div>
      <div className="spec-chip" style={{ padding: 16, marginTop: 16 }}>
        <div className="font-head" style={{ fontWeight: 600, marginBottom: 8 }}>Letzte Benachrichtigungen</div>
        {notifs.length === 0 && <div style={{ color: "var(--muted)", fontSize: 13 }}>Noch keine.</div>}
        {notifs.map((n, i) => (
          <div key={i} style={{ fontSize: 12, padding: "5px 0", borderBottom: "1px solid rgba(255,255,255,0.04)", display: "flex", gap: 10 }}>
            <span style={{ color: n.ok ? "#5bd18a" : "#e05f5f" }}>{n.ok ? "✓" : "✗"}</span>
            <span style={{ color: "var(--muted)" }}>{new Date(n.time).toLocaleString("de-DE")}</span>
            <span>{n.subject}</span>{n.error && <span style={{ color: "#e05f5f" }}>{n.error}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function LogsPanel() {
  const [logs, setLogs] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => { axios.get(`${API}/admin/logs`).then((r) => setLogs(r.data.logs)); }, []);
  return (
    <div className="fade-in" data-testid="admin-logs-panel">
      {logs.length === 0 && <div style={{ color: "var(--muted)" }}>Noch keine Importe.</div>}
      <div style={{ display: "grid", gap: 12 }}>
        {logs.map((l) => (
          <div key={l.run_id} className="spec-chip" style={{ padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10, cursor: "pointer" }} onClick={() => setOpen(open === l.run_id ? null : l.run_id)}>
              <div>
                <span style={{ color: l.status === "success" ? "#5bd18a" : "#e05f5f", fontWeight: 700 }}>{l.status === "success" ? "✓ Erfolgreich" : "✗ Fehler"}</span>
                <span style={{ color: "var(--muted)", marginLeft: 10, fontSize: 13 }}>{new Date(l.started_at).toLocaleString("de-DE")} · {l.trigger}</span>
              </div>
              <div style={{ fontSize: 13, color: "var(--silver)" }}>
                {l.added ?? 0} neu · {l.updated ?? 0} aktual. · {l.removed ?? 0} entfernt · {l.plates_blurred ?? 0} Kennz. · {l.duration_sec}s
              </div>
            </div>
            {open === l.run_id && (
              <div style={{ marginTop: 12, borderTop: "1px solid var(--border)", paddingTop: 10, maxHeight: 260, overflowY: "auto" }}>
                {(l.events || []).map((e, i) => {
                  const st = EVENT_STYLE[e.kind] || EVENT_STYLE.info;
                  return <div key={i} style={{ fontSize: 12, padding: "5px 0", display: "flex", gap: 10 }}>
                    <span style={{ color: st.c, minWidth: 90 }}>{st.l}</span><span style={{ color: "var(--silver)" }}>{e.message}</span>
                  </div>;
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsPanel() {
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState(false);
  const [sched, setSched] = useState(null);
  const [newHour, setNewHour] = useState("");
  const [schedMsg, setSchedMsg] = useState("");
  useEffect(() => {
    axios.get(`${API}/admin/settings`).then((r) => setS(r.data));
    axios.get(`${API}/admin/schedule`).then((r) => setSched(r.data));
  }, []);
  const save = async () => {
    const r = await axios.put(`${API}/admin/settings`, { markup_percent: parseFloat(s.markup_percent), round_to: parseInt(s.round_to) });
    setS(r.data); setSaved(true); setTimeout(() => setSaved(false), 2000);
  };
  const addHour = () => {
    const h = parseInt(newHour);
    if (isNaN(h) || h < 0 || h > 23) { setSchedMsg("Uhrzeit 0–23 eingeben"); return; }
    if (sched.hours.includes(h)) { setSchedMsg("Uhrzeit bereits vorhanden"); return; }
    setSched({ ...sched, hours: [...sched.hours, h].sort((a, b) => a - b) }); setNewHour(""); setSchedMsg("");
  };
  const removeHour = (h) => setSched({ ...sched, hours: sched.hours.filter((x) => x !== h) });
  const saveSched = async () => {
    const r = await axios.put(`${API}/admin/schedule`, { hours: sched.hours, minute: parseInt(sched.minute) || 0 });
    setSched(r.data); setSchedMsg("Zeiten gespeichert ✓"); setTimeout(() => setSchedMsg(""), 2500);
  };
  if (!s || !sched) return <div className="spinner" />;
  return (
    <div className="fade-in" data-testid="admin-settings-panel" style={{ maxWidth: 460 }}>
      <div className="spec-chip" style={{ padding: 24 }}>
        <label style={{ fontSize: 13, color: "var(--gold)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Preis-Aufschlag (%)</label>
        <input data-testid="markup-input" type="number" step="0.5" value={s.markup_percent} onChange={(e) => setS({ ...s, markup_percent: e.target.value })} style={{ width: "100%", marginTop: 8 }} />
        <div style={{ color: "var(--muted)", fontSize: 12, marginTop: 6 }}>z. B. 20 = +20 % auf jeden Fahrzeugpreis (falls kein eigener Preis gesetzt ist).</div>

        <label style={{ fontSize: 13, color: "var(--gold)", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginTop: 20 }}>Runden auf (€)</label>
        <input data-testid="round-input" type="number" value={s.round_to} onChange={(e) => setS({ ...s, round_to: e.target.value })} style={{ width: "100%", marginTop: 8 }} />

        <button className="btn-gold" data-testid="save-settings-btn" onClick={save} style={{ marginTop: 22, width: "100%" }}>{saved ? "Gespeichert ✓" : "Speichern"}</button>
      </div>

      <div className="spec-chip" data-testid="schedule-panel" style={{ padding: 24, marginTop: 16 }}>
        <div className="font-head" style={{ fontSize: 18, fontWeight: 600 }}>Automatischer Import — Uhrzeiten</div>
        <div style={{ color: "var(--muted)", fontSize: 12, margin: "6px 0 14px" }}>Zu diesen Uhrzeiten (deutsche Zeit) wird täglich automatisch importiert.</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
          {sched.hours.map((h) => (
            <span key={h} data-testid={`schedule-chip-${h}`} className="spec-chip" style={{ padding: "6px 10px", display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14 }}>
              {String(h).padStart(2, "0")}:{String(sched.minute || 0).padStart(2, "0")}
              <button data-testid={`schedule-remove-${h}`} onClick={() => removeHour(h)} style={{ background: "none", border: "none", color: "#e05f5f", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>×</button>
            </span>
          ))}
          {sched.hours.length === 0 && <span style={{ color: "var(--muted)", fontSize: 13 }}>Keine Zeiten — Auto-Import deaktiviert.</span>}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input data-testid="schedule-hour-input" type="number" min="0" max="23" placeholder="Stunde 0–23" value={newHour} onChange={(e) => setNewHour(e.target.value)} style={{ width: 120 }} />
          <button className="btn-ghost" data-testid="schedule-add-btn" onClick={addHour} style={{ padding: "8px 14px", fontSize: 13 }}>+ Uhrzeit</button>
          <span style={{ fontSize: 12, color: "var(--muted)" }}>Minute</span>
          <input data-testid="schedule-minute-input" type="number" min="0" max="59" value={sched.minute || 0} onChange={(e) => setSched({ ...sched, minute: e.target.value })} style={{ width: 80 }} />
        </div>
        <button className="btn-gold" data-testid="schedule-save-btn" onClick={saveSched} style={{ marginTop: 16, width: "100%" }}>Zeiten speichern</button>
        {schedMsg && <div style={{ marginTop: 10, color: "var(--gold-bright)", fontSize: 13 }}>{schedMsg}</div>}
      </div>
    </div>
  );
}

function AdminInner() {
  const [tab, setTab] = useState("dashboard");
  const [status, setStatus] = useState(null);
  const [sched, setSched] = useState(null);
  const load = () => axios.get(`${API}/sync/status`).then((r) => setStatus(r.data));
  useEffect(() => {
    load(); const t = setInterval(load, 4000);
    axios.get(`${API}/admin/schedule`).then((r) => setSched(r.data));
    return () => clearInterval(t);
  }, [tab]);
  const onSync = async () => { await axios.post(`${API}/sync`); setTimeout(load, 2000); };
  const logout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    delete axios.defaults.headers.common["Authorization"];
    window.location.reload();
  };
  const schedText = sched && sched.hours?.length
    ? sched.hours.map((h) => `${String(h).padStart(2, "0")}:${String(sched.minute || 0).padStart(2, "0")}`).join(" · ")
    : "—";

  const ls = status?.last_sync || {};
  return (
    <div style={{ maxWidth: 1080, margin: "0 auto", padding: "36px 22px 80px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <h1 className="font-head" style={{ fontSize: 30, fontWeight: 700 }}>ADMIN <span className="gold-text">PORTAL</span></h1>
        <button className="btn-ghost" data-testid="admin-logout-btn" onClick={logout}
          style={{ padding: "8px 16px", fontSize: 13 }}>Abmelden</button>
      </div>
      <div className="gold-line" style={{ width: 90, margin: "10px 0 26px" }} />
      <AdminNav tab={tab} setTab={setTab} />

      {tab === "dashboard" && (
        <div className="fade-in">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 14 }}>
            <StatCard label="Sichtbar" value={status?.visible_cars ?? "—"} accent="var(--gold-bright)" />
            <StatCard label="Gesamt aktiv" value={status?.active_cars ?? "—"} />
            <StatCard label="Verkäufer" value={status?.sellers ?? "—"} />
            <StatCard label="Neu / Entfernt" value={`${ls.added ?? 0} / ${ls.removed ?? 0}`} />
          </div>
          <div style={{ marginTop: 22, color: "var(--muted)", fontSize: 14 }} className="spec-chip">
            <div style={{ padding: 4 }}>
              <b style={{ color: "var(--silver)" }}>Automatischer Import:</b> täglich um <b style={{ color: "var(--gold-bright)" }} data-testid="dashboard-schedule">{schedText} Uhr</b> (deutsche Zeit).<br />
              {ls.finished_at && <>Letzter Import: {new Date(ls.finished_at).toLocaleString("de-DE")} · Dauer {ls.duration_sec}s · {ls.plates_blurred ?? 0} Kennzeichen · {ls.blocked_images ?? 0} Bilder gesperrt.</>}
            </div>
          </div>
          <button className="btn-gold" data-testid="reprocess-btn" disabled={status?.running} onClick={async () => {
            if (window.confirm("Alle Bilder mit der aktuellen KI neu bearbeiten? (kein Apify-Abruf nötig, dauert einige Minuten)")) {
              await axios.post(`${API}/admin/reprocess`); setTimeout(load, 1500);
            }
          }} style={{ marginTop: 16, marginRight: 10 }}>Bilder neu bearbeiten (KI)</button>
          {status?.running && (
            <button className="btn-ghost" data-testid="dashboard-stop-btn" onClick={async () => { await axios.post(`${API}/admin/stop`); setTimeout(load, 1500); }}
              style={{ marginTop: 16, marginRight: 10, color: "#e05f5f", borderColor: "#e05f5f" }}>■ Laufenden Vorgang stoppen</button>
          )}
          <button className="btn-ghost" data-testid="reset-cars-btn" onClick={async () => {
            const ans = window.prompt("ACHTUNG: Das löscht ALLE importierten Fahrzeuge (Verkäufer bleiben). Zum Bestätigen bitte LÖSCHEN eintippen:");
            if (ans && ans.trim().toUpperCase() === "LÖSCHEN") {
              await axios.post(`${API}/admin/reset`); load();
            }
          }} style={{ marginTop: 16, color: "#e05f5f", borderColor: "#e05f5f" }}>Alle Fahrzeuge löschen</button>
        </div>
      )}
      {tab === "sellers" && <SellersPanel status={status} />}
      {tab === "import" && <ImportPanel status={status} onSync={onSync} />}
      {tab === "cars" && <CarsPanel />}
      {tab === "logs" && <LogsPanel />}
      {tab === "smtp" && <SmtpPanel />}
      {tab === "settings" && <SettingsPanel />}
    </div>
  );
}

function AdminLogin({ onOk }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await axios.post(`${API}/admin/login`, { password: pw });
      const token = r.data.token;
      localStorage.setItem(ADMIN_TOKEN_KEY, token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      onOk();
    } catch (e) {
      setErr(e.response?.status === 401 ? "Falsches Passwort." : "Anmeldung fehlgeschlagen.");
    } finally { setBusy(false); }
  };
  return (
    <div style={{ maxWidth: 380, margin: "80px auto", padding: "0 22px" }} className="fade-in">
      <h1 className="font-head" style={{ fontSize: 28, fontWeight: 700 }}>ADMIN <span className="gold-text">LOGIN</span></h1>
      <div className="gold-line" style={{ width: 80, margin: "10px 0 26px" }} />
      <form onSubmit={submit} className="spec-chip" style={{ padding: 24 }}>
        <label style={{ fontSize: 12, color: "var(--muted)" }}>Passwort</label>
        <input data-testid="admin-login-password" type="password" value={pw} autoFocus
          onChange={(e) => setPw(e.target.value)} placeholder="Admin-Passwort"
          style={{ width: "100%", marginTop: 6, marginBottom: 16 }} />
        <button className="btn-gold" data-testid="admin-login-btn" type="submit" disabled={busy || !pw}
          style={{ width: "100%", padding: "12px 0" }}>{busy ? "Anmelden…" : "Anmelden"}</button>
        {err && <div data-testid="admin-login-error" style={{ marginTop: 12, color: "#e05f5f", fontSize: 13 }}>{err}</div>}
      </form>
    </div>
  );
}

function Admin() {
  const [authed, setAuthed] = useState(null); // null=prüft, false=Login, true=ok
  useEffect(() => {
    const tok = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!tok) { setAuthed(false); return; }
    axios.get(`${API}/admin/me`).then((r) => setAuthed(!!r.data.authenticated)).catch(() => setAuthed(false));
  }, []);
  if (authed === null) return <div style={{ textAlign: "center", padding: 80, color: "var(--muted)" }}>Lädt…</div>;
  if (!authed) return <AdminLogin onOk={() => setAuthed(true)} />;
  return <AdminInner />;
}

function App() {
  return (
    <div className="App grain">
      <BrowserRouter>
        <Header />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/fahrzeug/:adId" element={<CarDetail />} />
          <Route path="/admin" element={<Admin />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
